/*
  Полоса действий над выбранными в реестре счетами - решение владельца
  16.10.2026: «В конвейер», «Сменить стадию», «Удалить»; 20.10.2026 к ним
  добавлены «Уведомления» - досудебное уведомление по каждому счёту.

  Тёмная полоса внизу - та же, что на экране обзвона (`pick-bar`): прилипает к
  низу окна, и листать реестр до конца ради кнопки не нужно. Кнопки видны по
  правам: смена стадии - у того, кто ведёт конвейер, удаление - у руководителя,
  юриста и агента сопровождения.

  «В конвейер» ничего не меняет в правилах очереди (решение владельца того же
  дня): конвейер открывается с выбранными счетами из очереди уже отмеченными, а
  про остальные говорит, почему их там нет.
*/

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { accounts, accountsGenitive } from '../api/format.js'
import { canAssemble } from './access.js'
import AccountsRemoveDialog from './AccountsRemoveDialog.jsx'
import NoticeBulkDialog from './NoticeBulkDialog.jsx'
import StageBulkDialog from './StageBulkDialog.jsx'

const NAMED = 5
/* Сколько отказов называть в сообщении по номерам. Остальные - числом. */

const LIMIT = 1000
/* Сколько счетов сервер принимает одним действием (`MAX_ACCOUNTS`). Больше -
   кнопки выключены и сказано почему: иначе отказ сервера встал бы на месте
   экрана конвейера целиком (находка проверки 16.10.2026). */

const NOTICE_LIMIT = 200
/* Уведомлений за одно нажатие (`schemas/notices.MAX_ACCOUNTS`, 20.10.2026):
   каждое - разбор счёта, расчёт и ссылка, и запрос ждёт, пока соберутся все. */

function refusals(rows, word) {
  if (rows.length === 0) return ''
  const named = rows
    .slice(0, NAMED)
    .map((row) => `№ ${row.number} - ${row.reason}`)
    .join('; ')
  const rest = rows.length > NAMED ? `; и ещё ${accounts(rows.length - NAMED)}` : ''
  return ` ${word} (${rows.length}): ${named}${rest}.`
}

export default function RegistrySelection({ pick, profile, onDone }) {
  const [dialog, setDialog] = useState(null)
  const navigate = useNavigate()
  const can = (permission) => profile.permissions.includes(permission)
  // Режим только чтения: кнопки видны, но выключены - отказ при нажатии
  // объяснял бы то, что видно заранее (так же у звонка в карточке).
  const tooMany = pick.list.length > LIMIT
  const locked = Boolean(profile.is_readonly) || tooMany

  if (pick.list.length === 0) {
    // Отбор сменился - выбор снят, и об этом сказано (аудит 28.10.2026, раздел 5.3).
    return pick.cleared > 0 ? (
      <div className="pick-bar" role="status">
        Отбор изменился, поэтому выбор снят: было выбрано {accounts(pick.cleared)}.
      </div>
    ) : null
  }

  function removed(result) {
    setDialog(null)
    pick.drop([...result.removable.map((row) => row.account_id)])
    const kept = result.refused.map((row) => ({ number: row.number, reason: row.reasons.join(', ') }))
    onDone({
      kind: kept.length ? 'warn' : 'ok',
      text: `Удалено: ${accounts(result.removable.length)}.${refusals(kept, 'Не удалены')}`,
    })
  }

  function staged(outcomes) {
    setDialog(null)
    const numbers = new Map(pick.list.map((row) => [row.account_id, row.number]))
    const changed = outcomes.filter((row) => row.changed)
    const kept = outcomes
      .filter((row) => !row.changed)
      .map((row) => ({ number: numbers.get(row.account_id) ?? '?', reason: row.reason }))
    pick.drop(changed.map((row) => row.account_id))
    onDone({
      kind: kept.length ? 'warn' : 'ok',
      text: `Стадия сменилась у ${accountsGenitive(changed.length)}.${refusals(kept, 'Без изменений')}`,
    })
  }

  function noticed(result) {
    setDialog(null)
    pick.drop(result.built.map((row) => row.account_id))
    const total = result.built.reduce((sum, row) => sum + row.documents, 0)
    const kept = result.refused.map((row) => ({ number: row.number ?? '?', reason: row.reason }))
    onDone({
      kind: kept.length ? 'warn' : 'ok',
      text:
        `Сформировано уведомлений: ${total} - для ${accountsGenitive(result.built.length)}, ` +
        `они в карточках счетов.${refusals(kept, 'Не сформированы')}`,
    })
  }

  return (
    <>
      <div className="pick-bar">
        <b>Выбрано: {pick.list.length}</b>
        {tooMany ? <span>за раз - не больше {LIMIT}, снимите часть отметок</span> : null}
        <button
          type="button"
          className="button button--quiet"
          disabled={tooMany}
          onClick={() =>
            navigate('/conveyor', { state: { chosen: pick.list.map((row) => row.account_id) } })
          }
        >
          В конвейер
        </button>
        {can('manage_stages') ? (
          <button
            type="button"
            className="button button--quiet"
            disabled={locked}
            onClick={() => setDialog('stage')}
          >
            Сменить стадию
          </button>
        ) : null}
        {can('build_documents') && canAssemble(profile) ? (
          <button
            type="button"
            className="button button--quiet"
            disabled={locked || pick.list.length > NOTICE_LIMIT}
            title={
              pick.list.length > NOTICE_LIMIT
                ? `Уведомления - не больше ${NOTICE_LIMIT} счетов за раз`
                : undefined
            }
            onClick={() => setDialog('notice')}
          >
            Уведомления
          </button>
        ) : null}
        {can('delete_accounts') ? (
          <button
            type="button"
            className="button button--quiet button--danger-quiet"
            disabled={locked}
            onClick={() => setDialog('remove')}
          >
            Удалить
          </button>
        ) : null}
        <button type="button" className="button button--quiet" onClick={() => pick.drop()}>
          Снять выбор
        </button>
      </div>

      {dialog === 'remove' ? (
        <AccountsRemoveDialog
          chosen={pick.list}
          onClose={() => setDialog(null)}
          onRemoved={removed}
        />
      ) : null}
      {dialog === 'stage' ? (
        <StageBulkDialog chosen={pick.list} onClose={() => setDialog(null)} onChanged={staged} />
      ) : null}
      {dialog === 'notice' ? (
        <NoticeBulkDialog chosen={pick.list} onClose={() => setDialog(null)} onBuilt={noticed} />
      ) : null}
    </>
  )
}
